import { Link, useParams } from 'react-router';

import { fetchStockTransferDetail } from '#/api/imsMgmt/receipts/stockTransfer/fetchDetail';
import { updateStockTransferRemark } from '#/api/imsMgmt/receipts/stockTransfer/updateRemark';
import { fetchWorkOrderStockTransferDetail } from '#/api/momMgmt/workOrder/transfer/fetchDetail';
import { Button } from '#/modules/commonComponents/button';
import PageShell from '#/modules/infra/layout/PageShell';
import useDocumentRemarkSave from '#/hooks/useDocumentRemarkSave';
import DocumentRemarkEditForm from '#/modules/imsMgmt/receipts/shared/DocumentRemarkEditForm';
import StockTransferMainInfoCard from '#/modules/imsMgmt/receipts/stockTransfer/components/StockTransferMainInfoCard';
import {
  getStockTransferApiErrorMessage,
  isStockTransferVersionConflict,
} from '#/modules/imsMgmt/receipts/stockTransfer/helper/apiError';
import { useStockTransferEntryRoutes } from '#/modules/imsMgmt/receipts/stockTransfer/helper/stockTransferEntryRoutes';
import { canEditStockTransferRemark } from '#/modules/imsMgmt/receipts/stockTransfer/helper/transferStatus';
import {
  stockTransferDetailQueryKey,
  stockTransferListQueryKeyPrefix,
  workOrderStockTransferDetailQueryKey,
  workOrderStockTransferListQueryKeyPrefix,
} from '#/utils/queryKeys';

const StockTransferRemarkPage = () => {
  const { id } = useParams();
  const transferRoutes = useStockTransferEntryRoutes();
  const isWorkOrderTransfer = transferRoutes.isWorkOrderTransfer;

  const {
    detail,
    detailQuery,
    isPending,
    saveRemark,
  } = useDocumentRemarkSave({
    id,
    fetchDetail: isWorkOrderTransfer
      ? fetchWorkOrderStockTransferDetail
      : fetchStockTransferDetail,
    updateRemark: updateStockTransferRemark,
    detailQueryKey: isWorkOrderTransfer
      ? workOrderStockTransferDetailQueryKey
      : stockTransferDetailQueryKey,
    listQueryKeyPrefix: isWorkOrderTransfer
      ? workOrderStockTransferListQueryKeyPrefix
      : stockTransferListQueryKeyPrefix,
    getErrorMessage: (error) => getStockTransferApiErrorMessage(error, '更新備註失敗'),
    isVersionConflict: isStockTransferVersionConflict,
    afterSaveNavigateTo: transferRoutes.detail(id),
  });

  const isLoading = detailQuery.isLoading;
  const isError = detailQuery.isError;
  const remarkAllowed = detail ? canEditStockTransferRemark(detail.status) : false;

  return (
    <PageShell variant="form">
      <div className="layout-shell__body">
        <div className="mx-auto w-full max-w-4xl space-y-4">
          <div className="create_edit_page__header">
            <h1 className="page_title mb-0!">
              <span className="border-l-5 border-primary-400 pl-2.5">編輯轉倉備註</span>
            </h1>
            <Button type="button" variant="outline" asChild>
              <Link to={transferRoutes.detail(id)}>返回詳情</Link>
            </Button>
          </div>

          {isLoading ? (
            <p className="text-sm text-slate-500">載入中…</p>
          ) : null}

          {isError ? (
            <p className="text-sm text-destructive">
              {getStockTransferApiErrorMessage(detailQuery.error, '找不到轉倉單')}
            </p>
          ) : null}

          {detail && !remarkAllowed ? (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">此狀態不可編輯備註。</p>
              <Button type="button" asChild>
                <Link to={transferRoutes.detail(id)}>前往詳情</Link>
              </Button>
            </div>
          ) : null}

          {detail && remarkAllowed ? (
            <>
              <StockTransferMainInfoCard detail={detail} />
              <DocumentRemarkEditForm
                initialRemark={detail.remark ?? ''}
                cancelTo={transferRoutes.detail(id)}
                isPending={isPending}
                onSave={saveRemark}
              />
            </>
          ) : null}
        </div>
      </div>
    </PageShell>
  );
};

export default StockTransferRemarkPage;
